/* eslint-disable @typescript-eslint/no-explicit-any */
import { Label } from "@/src/components/Text/Text";
import { P2PComponentsData } from "../../../P2PTrade/types";
import InputComponent from "@/src/components/Input/Input";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/shad/select";
import { TPaymentMethods } from "./Step2";
import { isEmpty } from "lodash";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/src/components/Button/Button";
import { Modal } from "@/src/components/Modal/Modal";
import { useTranslation } from "react-i18next";

interface Props {
  data: P2PComponentsData | undefined;
  paymentMethods: TPaymentMethods[];
  setPaymentMethods: React.Dispatch<React.SetStateAction<TPaymentMethods[]>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const paymentState = z.object({
  account_name: z.string().min(1),
  account_number: z.string().min(1),
  extra: z.string().min(1).optional(),
  id: z.string().min(1),
});

const AddSellPayment = ({ data, setPaymentMethods, open, setOpen }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.p2p.addSellPayment.${key}`);
  const {
    register,
    setValue,
    trigger,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<z.infer<typeof paymentState>>({
    resolver: zodResolver(paymentState),
  });

  const submit: SubmitHandler<z.infer<typeof paymentState>> = (data) => {
    trigger();
    if (isEmpty(errors)) {
      setPaymentMethods((old) => [...old, data]);
      setOpen(false);
      reset();
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      setter="state"
      title={trans("title")}
      footer={
        <div className="flex justify-end items-center mt-5 space-x-4">
          <Button onClick={() => setOpen(false)} variant={"ghost"}>
            {trans("form.buttons.back")}
          </Button>
          <Button onClick={handleSubmit(submit)}>
            {trans("form.buttons.submit")}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit(submit)} className="grid gap-5">
        <div>
          <Label>{trans("form.type.label")}</Label>
          <Select onValueChange={(val) => setValue("id", val)}>
            <SelectTrigger>
              <SelectValue placeholder={trans("form.type.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {data?.info.payment_methods.map((item, index) => (
                <SelectItem key={index} value={item?.id.toString()}>
                  <div className="flex items-center ">
                    <span className="text-lg mr-2">
                      <img
                        src={item?.image}
                        alt={item?.name}
                        className="h-6 w-6"
                      />
                    </span>
                    <span>{item?.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.id?.message && (
            <div className="text-red-500 text-xs">{errors.id?.message}</div>
          )}
        </div>
        <InputComponent
          label={trans("form.accountName.label")}
          placeholder={trans("form.accountName.placeholder")}
          {...register("account_name")}
          errors={errors.account_name?.message}
        />
        <InputComponent
          label={trans("form.accountNumber.label")}
          placeholder={trans("form.accountNumber.placeholder")}
          {...register("account_number")}
          errors={errors.account_number?.message}
        />
        <div>
          <Label>{trans("form.extra.label")}</Label>
          <textarea
            {...register("extra", { required: true })}
            className="bg-input-field dark:bg-grey-600 dark:text-white border-gray-200 border rounded-md w-full p-2 md:p-4 focus:border-gray-300 "
            placeholder={trans("form.extra.placeholder")}
            cols={30}
            rows={5}
          />
          {errors.extra?.message && (
            <div className="text-red-500 text-xs">{errors.extra?.message}</div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddSellPayment;
